import { AccessTypeTag } from '@security/access/AccessTypeTag';
import { AddPermissionObjectPicker } from '../AddPermissionObjectPicker';
import { camelCase } from 'lodash';
import { CloseOutlined } from '@ant-design/icons';
import { ColumnType } from 'antd/es/table';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { ObjectHoverTooltip } from '../ObjectHoverTooltip';
import { PermissionsTag } from './PermissionsTag';
import React from 'react';
import { SalesforceLogo } from '@ui/icons';

import { AccessObject, AccessType, PermissionRecords } from '@sonar-software/sonar-core';
import { Button, Space, Tooltip } from 'antd';

export function getAccessObjectColumns<R extends PermissionRecords>(
	accessType: 'object' | 'field',
	dynamicColumns: Record<string, AccessObject>,
	highlightDifferences: boolean = false,
	additionalColumnIds?: Array<string>,
	onAddPermissionObject?: (label: string, type: AccessType, value: string) => void,
	onRemovePermissionObject?: (accessKey: string) => void
): Array<ColumnType<R>> {
	const showAddPermissionButton = Boolean(onAddPermissionObject);
	const isNewColumn = (col: string): boolean => additionalColumnIds?.includes(col) ?? false;

	const loadedColumns = Object.keys(dynamicColumns).filter((col) => !isNewColumn(col));
	const loadedObjectsCount = loadedColumns.length;

	const renderAccessObjectTitle = (access: string) => {
		const obj = dynamicColumns[ access ];
		const canRemove = Boolean(onRemovePermissionObject) && loadedObjectsCount > 0;

		const titleContent =
			<Space direction='vertical'>
				<Space>
					{ obj.name }
					<Tooltip
						placement='bottom'
						title={ COPY_SPECIFIC.VIEW_IN_SALESFORCE }>
						{ obj.sourceUrl &&
							<a
								href={ obj.sourceUrl }
								id={ access }
								rel='noopener noreferrer'
								style={ { display: 'flex' } }
								target='_blank'>
								<SalesforceLogo title='' />
							</a> }
					</Tooltip>
					{ canRemove &&
						<Tooltip
							placement='bottom'
							title='Remove column'>
							<Button
								icon={ <CloseOutlined /> }
								onClick={ () => onRemovePermissionObject?.(access) }
								size='small'
								type='text' />
						</Tooltip> }
				</Space>
				<AccessTypeTag accessType={ obj.type } />
			</Space>;

		return (
			<ObjectHoverTooltip
				accessObject={ obj }
				open={ obj.type === AccessType.User }>
				{ titleContent }
			</ObjectHoverTooltip>
		);
	};
	const columns: Array<ColumnType<R>> = Object.keys(dynamicColumns).map((access) => ({
		align: 'center',
		dataIndex: 'permissions',
		key: access,
		render: (permissions: Record<string, Array<string>>) => {
			const accessPermissions = permissions[ access ] ?? [];
			const newCol = isNewColumn(access);

			let differences: string[] | undefined;

			if (highlightDifferences && !newCol) {
				const allPermissions = new Set<string>();
				const permissionCounts: Record<string, number> = {};

				Object.keys(permissions).forEach((obj) => {
					if (!isNewColumn(obj)) {
						permissions[ obj ].forEach((permission) => {
							allPermissions.add(permission);
							permissionCounts[ permission ] = (permissionCounts[ permission ] || 0) + 1;
						});
					}
				});

				differences = Array.from(allPermissions)
					.filter((permission) => permissionCounts[ permission ] !== loadedObjectsCount)
					.map(camelCase);
			}

			const formattedPermissions = newCol ?
				{} :
				Object.fromEntries(
					accessPermissions.map((permission) => [ camelCase(permission), '' ])
				);

			return (
				<PermissionsTag
					className={ newCol ? 'grayed-permissions' : undefined }
					direction='vertical'
					noncongruent={ differences }
					permissions={ formattedPermissions }
					permissionsType={ accessType } />
			);
		},
		title: renderAccessObjectTitle(access),
		width: 250
	}));

	const AddPermissionObjectComponent: React.FC = () =>
		<AddPermissionObjectPicker
			onSelect={ (label: string, type: AccessType, value: string) => {
				onAddPermissionObject?.(label, type, value);
			} }
			trigger='click'>
			<Button type='default'>
				{ COPY_SPECIFIC.ADD_PERMISSION_OBJECT }
			</Button>
		</AddPermissionObjectPicker>;

	const additionalColumn = showAddPermissionButton ?
		[
			{
				align: 'center',
				key: 'addPermissionObject',
				title: <AddPermissionObjectComponent />,
				width: 210
			}
		  ] :
		[ {} ];

	return [ ...columns, ...additionalColumn ];
}
